import { IClassListItem } from "../model/respone/classes/IClassListItem";

export interface ISongHanhMissing {
    /** id_mon các môn song hành bắt buộc còn thiếu (chưa đăng ký). */
    missingMonIds: number[];
    /** Tên các môn còn thiếu — hiển thị cảnh báo. */
    missingMonNames: string[];
}

/**
 * Song hành có tính BẮC CẦU (A→B, B→C ⇒ đăng ký A cần cả B và C).
 *
 * Từ tập lớp đã đăng ký → map sang môn → tính TRANSITIVE CLOSURE theo `mon_song_hanh_ids`
 * (cạnh trực tiếp mỗi môn, backend trả trong danh sách lớp) → trừ các môn đã đăng ký
 * = tập môn song hành còn thiếu. Guard `visited` chống chu trình (A→B→A).
 *
 * Mirror backend `RegistrationService.ChotDuLieuCoreAsync` (guard 1c) — FE dùng để cảnh báo
 * sớm + chặn nút "Lưu dữ liệu"; backend vẫn là source of truth khi chốt.
 */
export function computeMissingSongHanh(
    list: IClassListItem[],
    registeredClassIds: number[]
): ISongHanhMissing {
    // Gộp đồ thị + tên môn + map classId→môn từ toàn bộ danh sách lớp.
    // Đồ thị song hành định nghĩa trên MÔN GỐC (CTDT). Lớp là môn tương đương (id_mon_goc>0) → map
    // về môn gốc để 1 môn song hành yêu cầu được thoả khi SV đăng ký môn tương đương của nó.
    const edges = new Map<number, number[]>();     // môn gốc → môn song hành trực tiếp
    const monName = new Map<number, string>();
    const classToMon = new Map<number, number>();
    for (const c of list) {
        const eff = (c.id_mon_goc && c.id_mon_goc > 0) ? c.id_mon_goc : c.id_mon;
        classToMon.set(c.classId, eff);
        if (c.ten_mon) monName.set(c.id_mon, c.ten_mon);
        // Cạnh khoá theo MÔN GỐC (eff): lớp môn tương đương C' mang cạnh của môn gốc C (backend đã
        // tính mon_song_hanh_ids theo idMonForRule) → không mất cạnh transitive khi C không có lớp riêng.
        if (c.mon_song_hanh_ids && c.mon_song_hanh_ids.length > 0) {
            edges.set(eff, c.mon_song_hanh_ids);
        }
    }

    // Tập môn đã đăng ký.
    const registeredMons = new Set<number>();
    for (const id of registeredClassIds) {
        const m = classToMon.get(id);
        if (m != null) registeredMons.add(m);
    }

    // BFS transitive closure — đi theo cạnh song hành kể cả sang môn CHƯA đăng ký.
    const required = new Set<number>();
    const queue = [...registeredMons];
    while (queue.length > 0) {
        const next = edges.get(queue.shift()!);
        if (!next) continue;
        for (const m of next) {
            if (!required.has(m)) {
                required.add(m);
                queue.push(m);
            }
        }
    }

    const missingMonIds = [...required].filter(m => !registeredMons.has(m));
    const missingMonNames = missingMonIds.map(m => monName.get(m) ?? `#${m}`);
    return { missingMonIds, missingMonNames };
}
